import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Search,
    Lightbulb,
    CheckCircle2,
    Loader2,
    LayoutDashboard,
    RotateCcw,
    FileText,
    BrainCircuit,
    ChevronRight,
    Scale,
    FileSearch,
    Dna,
    ShieldAlert,
    Info,
    History,
    Target
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { useAgentExecution } from "@/contexts/AgentExecutionContext";
import { Badge } from "../../components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { UsageTracker } from "@/lib/services/usage-tracker";

// ─── Types ───────────────────────────────────────────────────────────────────

interface PatentResult {
    status?: "processing" | "completed" | "failed" | "rejected";
    message?: string;
    guard_rail_details?: {
        is_valid: boolean;
        confidence: number;
        reason: string;
    };
    patentability_score?: number;
    novelty_assessment?: string;
    inventive_step_analysis?: string;
    prior_art_findings?: Array<{
        title: string;
        relevance: string;
        publication_number: string;
        description: string;
    }>;
    recommendations?: Array<{
        title: string;
        risk_level: "high" | "medium" | "low";
        strategy: string;
    }>;
}

interface PatentForm {
    invention_title: string;
    technical_field: string;
    technical_problem: string;
    solution_description: string;
    key_features: string[];
}

// ─── Premium Styles ──────────────────────────────────────────────────────────

function PremiumStyles() {
    return (
        <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

      .pv2-root {
        font-family: 'Inter', system-ui, -apple-system, sans-serif;
        background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 50%, #f3f4f6 100%);
        min-height: 100vh;
      }

      .pv2-header {
        background: linear-gradient(135deg, #312e81 0%, #3730a3 40%, #4338ca 100%);
        padding: 2rem 2.5rem;
        position: relative;
        overflow: hidden;
      }
      .pv2-header::before {
        content: '';
        position: absolute;
        inset: 0;
        background: radial-gradient(circle at 20% 80%, rgba(199,210,254,0.15) 0%, transparent 60%);
      }
      .pv2-header-content { position: relative; z-index: 1; }

      .pv2-body { display: flex; min-height: calc(100vh - 140px); }
      .pv2-form-panel {
        width: 35%;
        min-width: 360px;
        background: #fff;
        border-right: 1px solid #c7d2fe;
        padding: 2rem;
        overflow-y: auto;
      }
      .pv2-analytics-panel { flex: 1; padding: 2rem; overflow-y: auto; }

      @media (max-width: 768px) {
        .pv2-header {
          padding: 1.25rem 1rem !important;
        }
        .pv2-body {
          flex-direction: column !important;
          min-height: auto !important;
        }
        .pv2-form-panel {
          width: 100% !important;
          min-width: 100% !important;
          border-right: none !important;
          border-bottom: 1px solid #c7d2fe !important;
          padding: 1.25rem !important;
        }
        .pv2-analytics-panel {
          width: 100% !important;
          padding: 1.25rem !important;
        }
      }

      .pv2-field-group { margin-bottom: 1.5rem; animation: slideUp 0.4s ease-out; }
      .pv2-label {
        font-size: 0.7rem;
        font-weight: 900;
        color: #3730a3;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.4rem;
      }

      .pv2-card {
        background: #fff;
        border: 1px solid #eef2ff;
        border-radius: 1.5rem;
        padding: 1.5rem;
        box-shadow: 0 4px 20px -5px rgba(67,56,202,0.05);
      }

      @keyframes slideUp { 
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}</style>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function PatentSearchV2() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const apiClient = useApiClient();
    const { startPatentRun, getRun } = useAgentExecution();
    const isMobile = useIsMobile();
    const [mobileTab, setMobileTab] = useState<"form" | "results">("form");

    const [form, setForm] = useState<PatentForm>({
        invention_title: "",
        technical_field: "IA & Software",
        technical_problem: "",
        solution_description: "",
        key_features: [""]
    });

    const runId = "patent-run";
    const currentRun = getRun(runId);
    const loading = currentRun?.status === "loading";
    const result = currentRun?.metadata as PatentResult;

    const handleRunAnalysis = async () => {
        if (!form.invention_title.trim() || !form.solution_description.trim()) {
            toast.error("Por favor, complete el título y la descripción técnica.");
            return;
        }

        // Track usage for dashboard
        if (user) {
            UsageTracker.trackIPAssistant(user.id, "Búsqueda de patentes iniciada", form.technical_field, undefined);
        }

        if (isMobile) {
            setMobileTab("results");
        }

        await startPatentRun(runId, form, apiClient, user?.id || null);
    };

    const addFeature = () => setForm(f => ({ ...f, key_features: [...f.key_features, ""] }));
    const updateFeature = (i: number, val: string) => {
        const newFeatures = [...form.key_features];
        newFeatures[i] = val;
        setForm(f => ({ ...f, key_features: newFeatures }));
    };

    return (
        <div className="pv2-root">
            <PremiumStyles />

            <div className="pv2-header">
                <div className="pv2-header-content">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center border border-white/20">
                                <Lightbulb className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-black text-white tracking-tighter uppercase italic">Asistente de Patentes V2</h1>
                                <p className="text-indigo-200 text-[10px] font-bold tracking-widest uppercase opacity-70">Intellectual Property & Innovation Engine</p>
                            </div>
                        </div>
                        <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate("/dashboard")}>
                            <LayoutDashboard className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Mobile Tab Switcher */}
            {isMobile && (result || loading) && (
                <div className="bg-white border-b border-indigo-100 p-2 sticky top-0 z-30 shadow-sm">
                    <div className="flex items-center gap-1 bg-indigo-50 p-1 rounded-xl">
                        <button
                            type="button"
                            onClick={() => setMobileTab("form")}
                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all ${
                                mobileTab === "form"
                                    ? "bg-white text-indigo-800 shadow-sm"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            📝 Invención
                        </button>
                        <button
                            type="button"
                            onClick={() => setMobileTab("results")}
                            className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                                mobileTab === "results"
                                    ? "bg-white text-indigo-800 shadow-sm"
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            <Lightbulb className="h-3.5 w-3.5" />
                            <span>Patentabilidad</span>
                            {result?.patentability_score !== undefined ? (
                                <span className="bg-indigo-600 text-white text-[10px] px-1.5 py-0.2 rounded-full font-black">
                                    {result.patentability_score}%
                                </span>
                            ) : loading ? (
                                <Loader2 className="h-3 w-3 animate-spin text-indigo-600" />
                            ) : null}
                        </button>
                    </div>
                </div>
            )}

            <div className="pv2-body">
                {(!isMobile || !result || mobileTab === "form") && (
                <div className="pv2-form-panel">
                    <div className="space-y-6">
                        <div className="pv2-field-group">
                            <div className="pv2-label"><FileText className="h-4 w-4" /> Título de la Invención</div>
                            <Input
                                value={form.invention_title}
                                onChange={(e) => setForm(f => ({ ...f, invention_title: e.target.value }))}
                                placeholder="Ej: Sistema de compresión neuronal"
                                className="border-indigo-100 rounded-xl"
                            />
                        </div>

                        <div className="pv2-field-group">
                            <div className="pv2-label"><Dna className="h-4 w-4" /> Campo Técnico</div>
                            <Select value={form.technical_field} onValueChange={(v) => setForm(f => ({ ...f, technical_field: v }))}>
                                <SelectTrigger className="border-indigo-100 rounded-xl">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="IA & Software">IA & Software</SelectItem>
                                    <SelectItem value="Biotecnología">Biotecnología</SelectItem>
                                    <SelectItem value="Hardware & Electrónica">Hardware & Electrónica</SelectItem>
                                    <SelectItem value="Mecánica">Mecánica</SelectItem>
                                    <SelectItem value="Farmacéutica">Farmacéutica</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="pv2-field-group">
                            <div className="pv2-label"><Target className="h-4 w-4" /> Descripción de la Solución</div>
                            <Textarea
                                value={form.solution_description}
                                onChange={(e) => setForm(f => ({ ...f, solution_description: e.target.value }))}
                                placeholder="Describa cómo funciona su invención y qué la hace única..."
                                className="border-indigo-100 min-h-[120px] rounded-xl text-sm"
                            />
                        </div>

                        <div className="pv2-field-group">
                            <div className="pv2-label"><BrainCircuit className="h-4 w-4" /> Características Clave</div>
                            <div className="space-y-2">
                                {form.key_features.map((feat, i) => (
                                    <Input
                                        key={i}
                                        value={feat}
                                        onChange={(e) => updateFeature(i, e.target.value)}
                                        placeholder={`Característica #${i + 1}`}
                                        className="border-indigo-100 h-9 rounded-lg text-xs"
                                    />
                                ))}
                                <Button variant="ghost" onClick={addFeature} className="text-indigo-600 p-0 h-6 text-[10px] font-black uppercase">+ Agregar Característica</Button>
                            </div>
                        </div>

                        <Button
                            className="w-full h-12 bg-indigo-900 hover:bg-indigo-800 text-white font-black rounded-xl gap-2 shadow-xl shadow-indigo-100"
                            onClick={handleRunAnalysis}
                            disabled={loading}
                        >
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                            Analizar Patentabilidad
                        </Button>

                        {currentRun?.traceSteps && (
                            <div className="mt-8 space-y-3 px-2 border-t pt-6">
                                <h4 className="text-[10px] font-black text-indigo-800 uppercase tracking-widest mb-4">Pipeline de Análisis IP</h4>
                                {currentRun.traceSteps.map((s, i) => (
                                    <div key={i} className="flex items-center gap-3 animate-in fade-in slide-in-from-left-2">
                                        <span className="text-lg">{s.emoji}</span>
                                        <span className="text-[10px] font-bold text-slate-600 uppercase">{s.message}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                )}

                {(!isMobile || ((result || loading) && mobileTab === "results")) && (
                <div className="pv2-analytics-panel">
                    {!result && !loading ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mb-6">
                                <FileSearch className="h-12 w-12 text-indigo-200" />
                            </div>
                            <h2 className="text-2xl font-black text-indigo-900 tracking-tight">Evaluación de Propiedad Intelectual</h2>
                            <p className="text-slate-400 mt-2 max-w-sm font-medium text-sm">
                                Descubra si su invención cumple con los requisitos de novedad y actividad inventiva utilizando modelos neuronales avanzados.
                            </p>
                        </div>
                    ) : result?.status === 'rejected' ? (
                        <div className="max-w-2xl mx-auto py-12">
                            <div className="pv2-card border-t-4 border-t-red-500 shadow-2xl">
                                <div className="flex items-start gap-6 mb-8">
                                    <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center text-red-600">
                                        <ShieldAlert className="h-9 w-9" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-black text-slate-900 mb-2">Análisis No Procede</h3>
                                        <p className="text-slate-500 font-medium leading-relaxed">
                                            "{result.guard_rail_details?.reason || result.message}"
                                        </p>
                                    </div>
                                </div>
                                <Button className="w-full bg-slate-900 text-white font-bold h-12 rounded-xl" onClick={() => window.location.reload()}>
                                    Nuevo Análisis
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-700">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Score Widget */}
                                <div className="pv2-card flex flex-col items-center justify-center p-8 bg-gradient-to-br from-white to-indigo-50/30">
                                    <div className="pv2-label mb-4">Probabilidad de Registro</div>
                                    <div className="relative w-32 h-32 flex items-center justify-center">
                                        <svg className="absolute inset-0 w-full h-full -rotate-90">
                                            <circle cx="64" cy="64" r="58" stroke="#f5f3ff" strokeWidth="10" fill="none" />
                                            <circle
                                                cx="64" cy="64" r="58" stroke="#4f46e5" strokeWidth="10" fill="none"
                                                strokeDasharray="364"
                                                strokeDashoffset={364 - (364 * (result?.patentability_score || 0) / 100)}
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                        <span className="text-3xl font-black text-indigo-900">{result?.patentability_score || 0}%</span>
                                    </div>
                                    <div className="mt-6 flex flex-col items-center">
                                        <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Nivel de Novedad</span>
                                        <span className="font-bold text-slate-800">{result?.novelty_assessment || "Analizando..."}</span>
                                    </div>
                                </div>

                                {/* Prior Art */}
                                <div className="lg:col-span-2 space-y-4">
                                    <div className="flex items-center gap-2 px-2">
                                        <History className="h-5 w-5 text-indigo-600" />
                                        <h3 className="font-black text-indigo-900 text-xs uppercase italic">Estado de la Técnica (Prior Art)</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {result?.prior_art_findings?.map((p, i) => (
                                            <div key={i} className="pv2-card p-4 border-l-4 border-l-indigo-300">
                                                <div className="flex justify-between items-start mb-2">
                                                    <h4 className="font-bold text-slate-900 text-[10px] uppercase truncate flex-1 mr-2">{p.title}</h4>
                                                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[8px] font-black rounded uppercase">{p.relevance} SIM.</span>
                                                </div>
                                                <p className="text-[10px] text-slate-500 line-clamp-2 mb-2">{p.description}</p>
                                                <div className="flex items-center gap-1 text-[8px] font-bold text-slate-400">
                                                    <Search size={10} />
                                                    <span>{p.publication_number}</span>
                                                </div>
                                            </div>
                                        )) || <p className="text-xs text-slate-400 p-8 font-medium italic">Cruzando bases de datos de patentes internacionales...</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                )}
            </div>
        </div>
    );
}
